import {
  Button,
  Card,
  DatePicker,
  Dropdown,
  Input,
  Select,
  TimePicker,
} from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import moment from "moment";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../app/store";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import emptyUser from "../../../assets/images/empty_user.jpg";
import CustomTable from "../../../components/ui/CustomTable";
import IconButton from "../../../components/ui/IconButton";
import { BASE_API_BSRM } from "../../../constants/apiUrl";
import { API_URL_BSRM, dbDateFormat } from "../../../constants/constants";
import {
  useGetDistrictListQuery,
  useGetZoneListQuery,
} from "../../../features/bsrm/dashboard/dashboardApi";
import { useGetAllSupplierPartiesQuery } from "../../../features/bsrm/dealer/dealerApi";
import { useGetSingleCompanyQuery } from "../../../features/portal/companyOnboard/companyOnboardApi";
import useDownloadEmployeeXlsx from "../../../hooks/useDownloadExcelWithApi";
import useError from "../../../hooks/useError";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { disableFutureDates } from "../../../lib/time";
import {
  GetAllSupplierPartiesParams,
  GetAllSupplierPartiesResponseType,
} from "../../../types/bsrm/dealer.type";
import {
  addKeyInArray,
  customTimeConverter,
  formatSouthAsianNumber,
} from "../../../utils/utils";

type TimeFormat = {
  formTime?: string;
  toTime?: string;
};

export default function DealerList() {
  const [time, setTime] = useState<TimeFormat>();
  const [openDropdown, setOpenDropdown] = useState(false);
  const [applyDateFilter, setApplyDateFilter] = useState<{
    startDate?: string;
    endDate?: string;
  }>({});

  const [dateError, setDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState<
    string | undefined
  >(undefined);
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetAllSupplierPartiesParams>();

  const { user } = useAppSelector((state) => state.auth);
  const {
    isError: isCompanyError,
    error: companyError,
    data: companyData,
    isLoading: isCompanyLoading,
  } = useGetSingleCompanyQuery(user?.data?.company_id || "", {
    skip: !user?.data?.company_id,
  });

  const selectedSupplier = companyData?.data?.dynamicKeys?.find(
    (item: any) => item.id === Number(selectedSupplierId)
  );

  const [selectedZone, setSelectedZone] = useState<string | undefined>(
    undefined
  );
  const endTime = time?.toTime
    ? customTimeConverter(dayjs(time?.toTime, "HH:mm:ss").format("hh:mm:ss A"))
    : undefined;
  const {
    isError: getZoneListIsError,
    error: getZoneListError,
    data: getZoneListData,
  } = useGetZoneListQuery({
    supplierId: selectedSupplier?.dynamic_id,
    supplierKeyword: selectedSupplier?.dynamic_keyword,
  });

  const {
    isError: getDistrictListIsError,
    error: getDistrictListError,
    data: getDistrictListData,
  } = useGetDistrictListQuery({
    params: {
      zone: selectedZone,
      supplierId: selectedSupplier?.dynamic_id,
      supplierKeyword: selectedSupplier?.dynamic_keyword,
    },
  });
  // const {
  //   data: dealerList,
  //   isLoading: isDealerloading,
  //   isError: isDealerError,
  //   error: dealerError,
  // } = useGetDealerListQuery({
  //   ...filter,
  //   page: page,
  //   search: keyword,
  //   startDate: filter?.startDate
  //     ? `${filter?.startDate} ${time?.formTime || "00:00:00"}`
  //     : undefined,
  //   endDate: filter?.endDate
  //     ? `${filter?.endDate} ${endTime || "23:59:59"}`
  //     : undefined,
  //   supplierId: selectedSupplier?.dynamic_id,
  //   supplierKeyword: selectedSupplier?.dynamic_keyword,
  // });
  // const { startDate: sd, endDate: ed, ...filteredWithoutDates } = filter;

  const {
    data: getAllSupplierPartiesData,
    isError: getAllSupplierPartiesIsError,
    error: getAllSupplierPartiesError,
    isLoading: getAllSupplierPartiesIsLoading,
  } = useGetAllSupplierPartiesQuery({
    params: {
      zone: filter?.zone,
      district: filter?.district,
      party: "dealer",
      page: page,
      search: keyword,
      startDate: applyDateFilter?.startDate,
      endDate: applyDateFilter?.endDate,
      supplierId: selectedSupplier?.dynamic_id,
    },
  });

  const { loading: excelLoading, downloadXlsx } = useDownloadEmployeeXlsx();
  // useError(isDealerError, dealerError, "Get dealer list data fail");
  useError(
    getAllSupplierPartiesIsError,
    getAllSupplierPartiesError,
    "Get all supplier parties list data fail"
  );
  useError(
    getDistrictListIsError,
    getDistrictListError,
    "Get all district list data failed"
  );
  useError(
    getZoneListIsError,
    getZoneListError,
    "Get all zone list data failed"
  );
  useError(isCompanyError, companyError, "Get single caompany data failed");

  const handleStatementCancel = () => {
    handleFilter("startDate", undefined);
    handleFilter("endDate", undefined);
    setTime(undefined);
  };
  const handleZoneChange = (val: string) => {
    setSelectedZone(val);
    handleFilter("zone", val); // Optional: if you're doing filter logic
  };

  // Optional: handle district change
  const handleDistrictChange = (val: string) => {
    handleFilter("district", val);
  };

  const DateTimeSelector = (
    <div className="p-6 border rounded-lg shadow-md max-w-md mx-auto bg-white clear-button-hide">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-600 mb-1">From Date</label>
          <DatePicker
            value={
              filter?.startDate ? dayjs(filter?.startDate, dbDateFormat) : null
            }
            onChange={(e: Dayjs | null) => {
              const newStartDate = e
                ? dayjs(e).format(dbDateFormat)
                : undefined;

              // 🚫 Check if it's after the current end date
              if (
                newStartDate &&
                filter?.endDate &&
                dayjs(newStartDate).isAfter(dayjs(filter?.endDate))
              ) {
                handleFilter("startDate", undefined);
                setDateError("Start date cannot be later than end date.");
                return;
              }
              setEndDateError(null);
              setDateError(null);
              handleFilter("startDate", newStartDate);
            }}
            className="w-full"
            size="large"
            disabledDate={disableFutureDates}
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">Time</label>
          <TimePicker
            value={time?.formTime ? dayjs(time?.formTime, "HH:mm:ss") : null}
            onChange={(e: Dayjs | null) =>
              setTime((prev) => ({
                ...prev,
                formTime: dayjs(e).format("HH:mm:ss"),
              }))
            }
            className="w-full"
            format="hh:mm A"
            size="large"
            use12Hours
          />
        </div>
        {!!dateError && (
          <small className="text-red-500 block col-span-2">{dateError}</small>
        )}
        <div>
          <label className="block text-gray-600 mb-1">To Date</label>
          <DatePicker
            value={
              filter?.endDate ? dayjs(filter?.endDate, dbDateFormat) : null
            }
            onChange={(e: Dayjs | null) => {
              const newEndDate = e ? dayjs(e).format(dbDateFormat) : undefined;

              // 🚫 Check if it's before the current start date
              if (
                newEndDate &&
                filter?.startDate &&
                dayjs(newEndDate).isBefore(dayjs(filter?.startDate))
              ) {
                setDateError(null);
                setEndDateError(null);
                handleFilter("endDate", undefined);
                setEndDateError("End date cannot be earlier than start date.");
                return;
              }

              handleFilter("endDate", newEndDate);
              setEndDateError(null);
            }}
            className="w-full"
            size="large"
            disabledDate={disableFutureDates}
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">Time</label>
          <TimePicker
            value={time?.toTime ? dayjs(time?.toTime, "HH:mm:ss") : null}
            onChange={(e: Dayjs | null) => {
              setTime((prev) => ({
                ...prev,
                toTime: dayjs(e).format("HH:mm:ss"),
              }));
            }}
            className="w-full"
            format="hh:mm A"
            size="large"
            use12Hours
          />
        </div>{" "}
        {!!endDateError && (
          <small className="text-red-500 block col-span-2">
            {endDateError}
          </small>
        )}
      </div>
      <div className="flex justify-end gap-3 mt-6">
        <Button
          className="text-gray-600"
          onClick={() => {
            setOpenDropdown(false);
            handleStatementCancel();
            setApplyDateFilter({});
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            setOpenDropdown(false);
            setDateError(null);
            setEndDateError(null);
            setApplyDateFilter({
              startDate: filter?.startDate
                ? `${filter?.startDate} ${time?.formTime || "00:00:00"}`
                : undefined,
              endDate: filter?.endDate
                ? `${filter?.endDate} ${endTime || "23:59:59"}`
                : undefined,
            });
          }}
          color="primary"
          className="text-gray-600"
        >
          Apply
        </Button>
        {/* <Button
            type="primary"
            className="!text-primary !bg-[#147AFF33]"
            onClick={() => setOpenDropdown(false)}
          >
            View Statement
          </Button> */}
      </div>
    </div>
  );

  const columns: ColumnsType<GetAllSupplierPartiesResponseType> = [
    {
      title: <div className="text-nowrap">Dealer Code</div>,
      dataIndex: "dealerNo",
      align: "center",
      render: (val) => {
        return val ? val : "-";
      },
    },
    {
      title: <div style={{ width: "200px" }}>Dealer Name</div>,
      render: (val) => {
        return (
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 rounded-full">
              <img
                src={
                  val?.profile?.logoImage?.trim()
                    ? val.profile.logoImage
                    : emptyUser
                }
                alt="Dealer"
                className="rounded-full w-full h-full object-cover"
              />
            </div>

            <span className="flex-1">{val?.name}</span>
          </div>
        );
      },
    },
    {
      title: "Zone",
      dataIndex: "zone",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },
    {
      title: "District",
      dataIndex: "district",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },

    {
      title: <div className="text-nowrap">Wallet Number</div>,
      dataIndex: "merchantMsisdn",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Dealer Transaction</div>,

      align: "center",
      render: (val) => {
        return val ? formatSouthAsianNumber(val?.totalAmount) : "0";
      },
    },
    // {
    //   title: <div className="text-nowrap">Combined Transaction</div>,
    //   dataIndex: "combinedTransaction",
    //   align: "center",
    //   render: (val) => {
    //     return val ? val : "N/A";
    //   },
    // },

    {
      title: <div className="text-nowrap">Onboarding Date</div>,
      key: "date",
      align: "center",
      render: (val) => {
        return (
          <span className="text-nowrap">
            {val?.createdAt ? moment(val?.createdAt).format("DD/MM/YYYY") : "-"}
          </span>
        );
      },
    },
  ];

  const handleDownloadDealers = () => {
    downloadXlsx(
      API_URL_BSRM + BASE_API_BSRM.getAllSupplierParties,
      {
        zone: filter?.zone,
        district: filter?.district,
        party: "dealer",
        page: page,
        limit: 100000000000,
        search: keyword,
        startDate: applyDateFilter?.startDate,
        endDate: applyDateFilter?.endDate,
        supplierId: selectedSupplier?.dynamic_id,
      },

      `sPay_dealers_list_${moment().format("DD MMMM YYYY")}_${moment().format(
        "h.mm.ss A"
      )}`,
      "rows",
      (data: GetAllSupplierPartiesResponseType["data"]["rows"][0]) => {
        return {
          Zone: data?.zone ?? "-",
          District: data?.district ?? "-",
          "Dealer Code": data?.dealerNo ?? "-",
          "Dealer Name": data?.name ?? "-",
          // "Business Unit": data?.businessUnitName ?? "-",
          "Wallet Number": data?.merchantMsisdn ?? "-",
          "Dealer Transaction":
            formatSouthAsianNumber(data?.totalAmount) || "0",
          // "Combined Transaction": "N/A",
          "Onboarding Date": data?.createdAt
            ? moment(data?.createdAt).utc().format("DD/MM/YYYY")
            : "-",
          // "Transaction Date": data?.createdAt
          //   ? moment(data?.createdAt).utc().format("DD/MM/YYYY")
          //   : "-",
          // "Transaction Time": data?.createdAt
          //   ? moment(data?.createdAt).utc().format("hh:mm A")
          //   : "-",
          // "Transaction ID": data?.transactionId ?? "-",
          // "Payment Status":
          //   capitalize(data?.status === "success" ? "Success" : "pending") ??
          //   "Pending",

          // "Bank Account Name": data?.businessUnit?.bankAccountName ?? "-",
          // "Bank Account Number": data?.businessUnit?.bankAccountNo ?? "-",
          // "Bank Transfer Status":
          //   capitalize(data?.mStatus === "success" ? "Success" : "pending") ??
          //   "Pending",
          // "MR ID": data?.omrId ?? "-",
          // "MR Status":
          //   capitalize(
          //     data?.omrStatus === "success"
          //       ? "Success"
          //       : data?.omrStatus === "approved"
          //       ? "Approved"
          //       : "pending"
          //   ) ?? "Pending",
        };
      }
    );
  };

  useEffect(() => {
    const dynamicKeys = companyData?.data?.dynamicKeys;
    if (Array.isArray(dynamicKeys) && dynamicKeys.length > 0) {
      const firstId = String(dynamicKeys[0].id);
      setSelectedSupplierId((prev) => prev ?? firstId);
    }
  }, [companyData]);

  return (
    <>
      <SidebarLayout title="Dealer List">
        <Card className="mb-4 border border-light rounded">
          <div className="text-black text-xl font-medium leading-tight flex items-center gap-3">
            <div>
              Total Dealer: {getAllSupplierPartiesData?.data?.rows?.length || 0}
            </div>
          </div>
        </Card>

        <CustomTable
          loading={getAllSupplierPartiesIsLoading || isCompanyLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={getAllSupplierPartiesData?.data?.count}
          dataSource={addKeyInArray(
            getAllSupplierPartiesData?.data?.rows || []
          )}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Select
                placeholder="Select Supplier Key"
                showSearch
                className="!min-w-[110px]"
                suffixIcon={<SelectDownIcon />}
                style={{ width: "100%" }}
                // allowClear
                optionFilterProp="label"
                options={companyData?.data?.dynamicKeys?.map((item: any) => ({
                  label: item.dynamic_key_name,
                  value: String(item.id),
                }))}
                value={selectedSupplierId} // 👈 controlled value
                onChange={(value) => setSelectedSupplierId(value)}
              />

              <Dropdown
                overlay={DateTimeSelector}
                trigger={["click"]}
                open={openDropdown}
                onOpenChange={setOpenDropdown}
              >
                <IconButton className="flex items-center text-[#7a8799]">
                  {applyDateFilter &&
                  (filter?.startDate ||
                    filter?.endDate ||
                    time?.formTime ||
                    time?.toTime) ? (
                    <>
                      {filter?.startDate && (
                        <span>
                          {dayjs(filter.startDate).format("DD/MM/YYYY")}
                          {time?.formTime &&
                            ` ${dayjs(time.formTime, "HH:mm:ss").format(
                              "hh:mm A"
                            )}`}
                        </span>
                      )}
                      {" - "}
                      {filter?.endDate && (
                        <span>
                          {dayjs(filter.endDate).format("DD/MM/YYYY")}
                          {time?.toTime &&
                            ` ${dayjs(time.toTime, "HH:mm:ss").format(
                              "hh:mm A"
                            )}`}
                        </span>
                      )}
                    </>
                  ) : (
                    "Date & Time"
                  )}
                </IconButton>
              </Dropdown>

              <Input
                placeholder="Search dealer’s name or code"
                className="text-[#AFBACA] !w-[280px]"
                prefix={<SearchIcon />}
                onChange={handleSearch}
                allowClear
              />
              <Select
                placeholder="Zone"
                suffixIcon={<SelectDownIcon />}
                showSearch
                allowClear
                className="min-w-[100px]"
                value={filter?.zone}
                options={
                  getZoneListData?.data?.rows?.map((zone: string) => ({
                    label: zone,
                    value: zone,
                  })) || []
                }
                onChange={(val) => {
                  handleZoneChange(val);
                  handleDistrictChange(undefined as unknown as string);
                }}
              />

              <Select
                placeholder="District"
                suffixIcon={<SelectDownIcon />}
                showSearch
                allowClear
                className="min-w-[100px]"
                disabled={!selectedZone}
                value={filter?.district}
                options={
                  getDistrictListData?.data?.rows?.map((district: string) => ({
                    label: district,
                    value: district,
                  })) || []
                }
                onChange={handleDistrictChange}
              />
              <Button
                type="primary"
                className="middle-button"
                loading={excelLoading}
                onClick={handleDownloadDealers}
              >
                Export
              </Button>
            </>
          }
        />
      </SidebarLayout>
    </>
  );
}
